// src/components/InvitePage.js
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getCommissionInfo, getCommissionUser } from '../api/productApi';
import '../styles/InvitePage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faCopy, faGift, faMoneyBillWave, faCoins, faWallet, faHistory, faFolderOpen, faChevronRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import AlertModal from './AlertModal'; // 引入 AlertModal 组件

const InvitePage = ({ onLoginClick }) => {
  const [commissionInfo, setCommissionInfo] = useState(null);
  const [commissionUsers, setCommissionUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false); // 新增状态控制弹窗
  const { isLoggedIn } = useSelector((state) => state.user); // 从 Redux 获取登录状态

  useEffect(() => {
    const fetchData = async () => {
      try {
        const uid = localStorage.getItem('userId'); 
        if (!uid) {
          throw new Error('No uid found in localStorage');
        }

        const infoResponse = await getCommissionInfo({ uid });
        console.log('getCommissionInfo response:', infoResponse.data[0]);
        setCommissionInfo(infoResponse.data[0]);

        const userResponse = await getCommissionUser({ uid });
        console.log('getCommissionUser response:', userResponse.data);
        setCommissionUsers(userResponse.data);
      } catch (err) {
        console.error('Failed to fetch data:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isLoggedIn) {
      fetchData(); // 已登录时才发起请求
    } else {
      // 未登录时保持 loading 并触发登录弹窗
      setLoading(true);
      if (onLoginClick) {
        onLoginClick(); // 调用父组件传入的登录弹窗函数
      }
    }
  }, [isLoggedIn, onLoginClick]); // 依赖 isLoggedIn 和 onLoginClick

  const handleCopyLink = () => {
    if (!isLoggedIn) {
      if (onLoginClick) onLoginClick(); // 未登录时点击复制触发登录弹窗
      return;
    }
    const input = document.querySelector('.invite-link-input');
    input.select();
    document.execCommand('copy');

    const copyBtn = document.querySelector('.copy-btn');
    const originalContent = copyBtn.innerHTML;
    copyBtn.innerHTML = '<i class="fas fa-check"></i> 已复制';

    setTimeout(() => {
      copyBtn.innerHTML = originalContent;
    }, 2000);
  };

  // 新增处理提现按钮点击的函数
  const handleWithdrawClick = () => {
    if (!isLoggedIn) {
      if (onLoginClick) onLoginClick();
      return;
    }
    setIsAlertOpen(true); // 打开弹窗
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr.split(' ')[0];
  };

  // Loading 占位符组件
  const LoadingPlaceholder = () => (
    <div className="loading-placeholder">
      <div className="hero-banner">
        <h1 className="main-title">该功能近期维修升级中，暂不支持邀请</h1>
        <p className="subtitle">分享链接邀请好友，好友首次付费即可获得<span className="highlight">20%</span>奖励，持续收益！</p>
        <div className="banner-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <FontAwesomeIcon icon={faLink} />
            邀请链接
          </div>
        </div>
        <div className="card-body">
          <div className="invite-link-container">
            <div className="invite-link-input placeholder-box" style={{ height: '48px' }}></div>
            <button className="copy-btn" disabled={!isLoggedIn} onClick={handleCopyLink}>
              <FontAwesomeIcon icon={faCopy} />
              复制链接
            </button>
          </div>
          <div className="invite-tips">
            <div className="tip-item">
              <span className="tip-icon"><FontAwesomeIcon icon={faGift} /></span>
              <span className="tip-text">分享您的专属链接，好友首次付费您可获20%奖励，后续每次付费分享10%收益！</span>
            </div>
            <div className="tip-item">
              <span className="tip-icon"><FontAwesomeIcon icon={faMoneyBillWave} /></span>
              <span className="tip-text">提现要求：满30元可提现，邀请有年会员的需满100元！！</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <FontAwesomeIcon icon={faCoins} />
            邀请奖励金额
          </div>
        </div>
        <div className="card-body">
          <div className="rewards-overview">
            <div className="rewards-amount">
              <div className="rewards-value placeholder-box" style={{ width: '80px', height: '40px' }}></div>
              <div className="rewards-label">当前可提现奖励金额</div>
            </div>
            <button className="withdraw-btn" disabled>
              <FontAwesomeIcon icon={faWallet} />
              奖励提现
            </button>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value placeholder-box" style={{ width: '40px', height: '30px', margin: '0 auto' }}></div>
              <div className="stat-label">总推荐人数</div>
            </div>
            <div className="stat-card">
              <div className="stat-value placeholder-box" style={{ width: '40px', height: '30px', margin: '0 auto' }}></div>
              <div className="stat-label">已充值人数</div>
            </div>
            <div className="stat-card">
              <div className="stat-value placeholder-box" style={{ width: '40px', height: '30px', margin: '0 auto' }}></div>
              <div className="stat-label">总充值金额</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <FontAwesomeIcon icon={faHistory} />
            随心用邀请记录
          </div>
        </div>
        <div className="card-body">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>注册时间</th>
                  <th>充值时间</th>
                  <th>充值金额</th>
                  <th>佣金金额</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td><div className="placeholder-box" style={{ width: '80px', height: '20px' }}></div></td>
                    <td><div className="placeholder-box" style={{ width: '100px', height: '20px' }}></div></td>
                    <td><div className="placeholder-box" style={{ width: '100px', height: '20px' }}></div></td>
                    <td><div className="placeholder-box" style={{ width: '60px', height: '20px' }}></div></td>
                    <td><div className="placeholder-box" style={{ width: '60px', height: '20px' }}></div></td>
                    <td><div className="placeholder-box" style={{ width: '80px', height: '20px' }}></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer">
          <div className="pagination">
            <div className="page-info placeholder-box" style={{ width: '100px', height: '20px' }}></div>
            <div className="page-controls">
              <div className="page-btn active">1</div>
              <div className="page-btn">
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) return <div>错误: {error}</div>;

  return (
    <div className="container">
      {loading ? <LoadingPlaceholder /> : (
        <>
          <div className="hero-banner">
            <h1 className="main-title">
              邀请好友 
              {/* <span className="highlight">双重奖励</span> */}
            </h1>
            <p className="subtitle">
              分享链接邀请好友，好友付费即可获得<span className="highlight">20%</span>奖励，持续收益！
            </p>
            <div className="banner-decoration">
              <div className="decoration-circle circle-1"></div>
              <div className="decoration-circle circle-2"></div>
              <div className="decoration-circle circle-3"></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <FontAwesomeIcon icon={faLink} />
                邀请链接
              </div>
            </div>
            <div className="card-body">
              <div className="invite-link-container">
                <input
                  type="text"
                  className="invite-link-input"
                  value={commissionInfo?.commission_url || 'http://gpt2you.com/list#/index/u=9913/commission'}
                  readOnly
                />
                <button className="copy-btn" onClick={handleCopyLink}>
                  <FontAwesomeIcon icon={faCopy} />
                  复制链接
                </button>
              </div>
              <div className="invite-tips">
                <div className="tip-item">
                  <span className="tip-icon"><FontAwesomeIcon icon={faGift} /></span>
                  <span className="tip-text">
                    分享您的专属链接，好友首次付费您可获20%奖励，后续每次付费分享10%收益！
                  </span>
                </div>
                <div className="tip-item">
                  <span className="tip-icon"><FontAwesomeIcon icon={faMoneyBillWave} /></span>
                  <span className="tip-text">
                    提现要求：满30元可提现，邀请有年会员的需满100元！！
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <FontAwesomeIcon icon={faCoins} />
                邀请奖励金额
              </div>
            </div>
            <div className="card-body">
              <div className="rewards-overview">
                <div className="rewards-amount">
                  <div className="rewards-value">${commissionInfo?.commission_amount || '0'}</div>
                  <div className="rewards-label">当前可提现奖励金额</div>
                </div>
                <button className="withdraw-btn" onClick={handleWithdrawClick}>
                  <FontAwesomeIcon icon={faWallet} />
                  奖励提现
                </button>
              </div>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{commissionInfo?.user_num || '0'}</div>
                  <div className="stat-label">总推荐人数</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{commissionInfo?.pay_user_num || '0'}</div>
                  <div className="stat-label">已充值人数</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">${commissionInfo?.pay_amount || '0'}</div>
                  <div className="stat-label">总充值金额</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <FontAwesomeIcon icon={faHistory} />
                随心用邀请记录
              </div>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>用户</th>
                      <th>注册时间</th>
                      <th>充值时间</th>
                      <th>充值金额</th>
                      <th>佣金金额</th>
                      <th>状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionUsers.length === 0 ? (
                      <tr>
                        <td colSpan="6">
                          <div className="empty-state">
                            <div className="empty-icon">
                              <FontAwesomeIcon icon={faFolderOpen} />
                            </div>
                            <div className="empty-message">
                              暂无邀请记录，分享您的链接开始赚取奖励吧！
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      commissionUsers.map((user, index) => (
                        <tr key={user.num || index}>
                          <td>{user.uid}</td>
                          <td>{formatDate(user.uid_created_at)}</td>
                          <td>{formatDate(user.pay_created_at)}</td>
                          <td>${user.pay_amount || '-'}</td>
                          <td>${user.commission_amount || '-'}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                user.commission_status === '未提现' ? 'badge-pending' : 'badge-success'
                              }`}
                            >
                              {user.commission_status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer">
              <div className="pagination">
                <div className="page-info">共 {commissionUsers.length} 条记录</div>
                <div className="page-controls">
                  <div className="page-btn active">1</div>
                  <div className="page-btn">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 添加 AlertModal 组件 */}
          <AlertModal
            isOpen={isAlertOpen}
            message="该功能近期维修升级中，暂不支持邀请" // 替换为实际微信号
            onClose={() => setIsAlertOpen(false)} // 关闭弹窗
            confirmText="我知道了" // 自定义按钮文字
          />
        </>
      )}
    </div>
  );
};

export default InvitePage;